<script lang="ts">
  import ChevronDown from '@lucide/svelte/icons/chevron-down';
  import GripVertical from '@lucide/svelte/icons/grip-vertical';
  import Plus from '@lucide/svelte/icons/plus';
  import Settings2 from '@lucide/svelte/icons/settings-2';
  import Trash2 from '@lucide/svelte/icons/trash-2';

  import { getServices } from '../../app/context';
  import type { VisibleCategory } from '../../model/filter';
  import {
    addKink,
    moveCategory,
    removeCategory,
    renameCategory,
    setCollapsed,
  } from '../../model/ops';
  import { categoryProgress } from '../../model/stats';
  import type { Category, Kink } from '../../model/types';
  import { LIMITS } from '../../model/types';
  import { format, reorderMessages, strings } from '../../strings';
  import IconButton from '../../ui/IconButton.svelte';
  import InlineEdit from '../../ui/InlineEdit.svelte';
  import { reorderHandle, ReorderState } from '../../ui/reorder.svelte';
  import KinkRow from '../kink/KinkRow.svelte';

  interface Props {
    entry: VisibleCategory;
    /** While searching, cards can't be collapsed (matches are always shown). */
    collapsible: boolean;
    /** Set when cards can be reordered (edit mode, unfiltered list). */
    reorder?: ReorderState | null;
    oncomment: (kink: Kink, trigger: HTMLElement) => void;
    onedit: (category: Category, trigger: HTMLElement) => void;
  }

  let { entry, collapsible, reorder = null, oncomment, onedit }: Props = $props();

  const { store, view, toasts } = getServices();
  const id = $props.id();
  const category = $derived(entry.category);
  const progress = $derived(categoryProgress(category, store.doc.ratings[0]?.id ?? ''));
  const complete = $derived(progress.total > 0 && progress.rated === progress.total);
  const kinkReorder = new ReorderState();
  let newKink = $state('');

  const handle = $derived(
    reorder &&
      reorderHandle(reorder, {
        group: 'categories',
        id: category.id,
        label: category.name,
        index: () => store.doc.categories.findIndex((c) => c.id === category.id),
        count: () => store.doc.categories.length,
        move: (to) => store.apply((doc) => moveCategory(doc, category.id, to)),
        begin: () => {
          store.beginGesture();
        },
        end: (commit) => {
          store.endGesture(commit);
        },
        announce: (message) => {
          view.announce(message);
        },
        messages: reorderMessages,
      }),
  );

  function toggle() {
    store.apply((doc) => setCollapsed(doc, category.id, entry.expanded), { record: false });
  }

  function remove() {
    store.apply((doc) => removeCategory(doc, category.id));
    toasts.show(format.deleted(category.name), {
      action: { label: strings.undo, run: () => store.undo() },
    });
  }

  function add() {
    if (newKink.trim() === '') return;
    const name = newKink;
    store.apply((doc) => addKink(doc, category.id, { id: store.newId(), name }));
    newKink = '';
  }
</script>

<!-- Named directly: in edit mode the heading holds a "Rename …" button. -->
<section
  aria-label={category.name}
  data-reorder-group="categories"
  data-reorder-id={category.id}
  class={[
    'mb-3 rounded-card border border-line bg-surface',
    reorder?.active === category.id && 'ring-2 ring-focus',
  ]}
>
  <header class="flex items-start gap-2 px-3 pt-3 pb-2">
    {#if view.editMode}
      {#if handle}
        <button
          type="button"
          aria-label={format.reorder(category.name)}
          class="grid size-8 shrink-0 cursor-grab place-items-center rounded-md text-ink-muted hover:bg-surface-2 hover:text-ink aria-pressed:cursor-grabbing aria-pressed:text-focus pointer-coarse:size-11"
          {...handle}
        >
          <GripVertical size={18} aria-hidden="true" />
        </button>
      {/if}
      <div class="min-w-0 flex-1">
        <h2 class="text-base leading-tight font-semibold">
          <InlineEdit
            value={category.name}
            label={format.rename(category.name)}
            onsubmit={(name: string) =>
              store.apply((doc) => renameCategory(doc, category.id, name))}
          />
        </h2>
        {#if category.roles.length > 1}
          <p class="px-1 text-xs text-ink-muted italic">
            {category.roles.map((role) => role.name).join(', ')}
          </p>
        {/if}
      </div>
      <IconButton
        size="sm"
        icon={Settings2}
        label={format.editRoles(category.name)}
        onclick={(event: MouseEvent) => {
          if (event.currentTarget instanceof HTMLElement) onedit(category, event.currentTarget);
        }}
      />
      <IconButton
        size="sm"
        tooltipAlign="end"
        icon={Trash2}
        label={format.remove(category.name)}
        class="hover:text-danger"
        onclick={remove}
      />
    {:else}
      <div class="min-w-0 flex-1">
        <h2 class="text-base leading-tight font-semibold">
          {#if collapsible}
            <button
              type="button"
              aria-expanded={entry.expanded}
              aria-controls="{id}-body"
              class="group flex w-full items-start gap-1 rounded-md text-left"
              onclick={toggle}
            >
              <ChevronDown
                size={18}
                aria-hidden="true"
                class={[
                  'mt-px shrink-0 text-ink-muted transition-transform duration-150 group-hover:text-ink',
                  !entry.expanded && '-rotate-90',
                ]}
              />
              <span class="break-words">{category.name}</span>
            </button>
          {:else}
            <span class="break-words">{category.name}</span>
          {/if}
        </h2>
        {#if category.roles.length > 1}
          <p class={['text-xs text-ink-muted italic', collapsible && 'pl-[1.375rem]']}>
            {category.roles.map((role) => role.name).join(', ')}
          </p>
        {/if}
      </div>
      <span
        class={[
          'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
          complete ? 'bg-accent-soft text-ink' : 'bg-surface-2 text-ink-muted',
        ]}
        aria-label={format.progressLabel(progress.rated, progress.total)}
      >
        {format.progressShort(progress.rated, progress.total)}
      </span>
    {/if}
  </header>
  {#if entry.expanded || view.editMode}
    <div id="{id}-body" class="px-3 pb-2">
      {#if entry.kinks.length === 0 && !view.editMode}
        <p class="py-2 text-sm text-ink-muted">{strings.emptyCategory}</p>
      {:else}
        <ul>
          {#each entry.kinks as { kink, matches, dimmed } (kink.id)}
            <KinkRow
              {category}
              {kink}
              {matches}
              {dimmed}
              reorder={reorder ? kinkReorder : null}
              {oncomment}
            />
          {/each}
        </ul>
      {/if}
      {#if view.editMode}
        <form
          class="flex gap-2 border-t border-line pt-2"
          onsubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <input
            bind:value={newKink}
            aria-label="{strings.newKinkLabel}: {category.name}"
            placeholder={strings.addKinkPlaceholder}
            maxlength={LIMITS.name}
            class="h-9 min-w-0 flex-1 rounded-lg border border-line-strong/50 bg-surface px-3 text-base text-ink placeholder:text-ink-muted sm:text-sm pointer-coarse:h-11"
          />
          <IconButton type="submit" size="md" icon={Plus} label={strings.addKink} />
        </form>
      {/if}
    </div>
  {/if}
</section>
